import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { createNewDayjsObject } from './index';

export const DURATION_POLICY = { FIXED: 'FIXED', SELECTABLE_END: 'SELECTABLE_END' };

const range = (start, end) => {
  const result = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};

const disabledHoursAndMinutesFromWeekdaySchedule = (schedules, date) => {
  const allowedHours=[];
  let minutes = [];

  schedules.forEach(schedule=>{
    const { startHour, startMin, endHour, endMin } = scheduleToIntValues(schedule);
    // Collect allowed hours between schedule start & end
    for (let i=startHour; i<= endHour; i++) {
      allowedHours.push(i);
    }
    // If date parameter is either start or end hour, fill in the minutes
    if (date.hour() === startHour) {
      minutes = range(0, startMin);
    } else if (date.hour() === endHour) {
      minutes = range(endMin < 60 ? endMin + 1 : endMin, 60);
    }
  });
  return {
    disabledHours: () => range(0, 24).filter(hour=>!allowedHours.includes(hour)),
    disabledMinutes: () => minutes,
  };
};

const noneDisabled = {
  disabledHours: () => [],
  disabledMinutes: () => [],
};

// this can be returned when everything should be disabled
const allDisabled = {
  disabledHours: () => range(0, 24),
  disabledMinutes: () => range(0, 60),
};

// Disables the times after start + "selectable end max parking time" on the last allowed day
const disabledTimesAfterMaxEnd = (date, product, startDate, kiosk) => {
  const maxParkingTime = selectableEndMaxParkingTime(product, kiosk);
  if (!maxParkingTime) {
    return noneDisabled;
  }
  const maxEndDate = dayjs(startDate).add(maxParkingTime.asMilliseconds());
  if (maxEndDate.isBefore(date, 'day')) {
    return allDisabled;
  }
  if (!maxEndDate.isSame(date, 'day')) {
    return noneDisabled;
  }
  const lastHour = maxEndDate.hour();
  const lastMinute = date.hour() === lastHour ? maxEndDate.minute() : 60;
  return {
    disabledHours: () => range(lastHour + 1, 24),
    disabledMinutes: () => range(lastMinute + 1, 60),
  };
};

const combineDisabledTimes = (first, second) => ({
  disabledHours: () => [ ...first.disabledHours(), ...second.disabledHours() ],
  disabledMinutes: () => [ ...first.disabledMinutes(), ...second.disabledMinutes() ],
});

const sellingScheduleDisabledTimes = (date, product, startDate, kiosk, endDate=false) => {
  const schedule = product?.sellingSchedule;

  if (!date) {
    return noneDisabled;
  }
  if (isEmpty(schedule)) {
    return disabledTimesAfterMaxEnd(date, product, startDate, kiosk);
  }

  // Find a weekday from given date
  const weekday = date.format('dddd').toUpperCase();

  // Find a schedule for the day given as parameter
  const weekdaySchedule = schedule.filter(schedule => {
    if (!endDate) {
      return schedule.dayOfWeek === weekday;
    } else {
      // For end date, we must check if there are multiple schedules for same day
      // - we need to use the correct schedule where the start time matches
      if (schedule.dayOfWeek !== weekday) {
        return false;
      }
      const start = dayjs(startDate);
      // On a later day the permit continues over midnight, so the start time does not match any schedule of that day
      // and the schedule that starts at midnight is the one that applies
      if (!start.isSame(date, 'day')) {
        return isStartOfDay(schedule.startTime);
      }
      const { startHour, endHour } = scheduleToIntValues(schedule);
      return endHour >= start.hour() && startHour <= start.hour();
    }
  });
  // If we have a schedule we need to return hours & minutes "outside" the schedule
  // Also if kiosk has limit for max parking time we need to take that into account
  if (!isEmpty(weekdaySchedule)) {
    return combineDisabledTimes(
      disabledHoursAndMinutesFromWeekdaySchedule(weekdaySchedule, date),
      disabledTimesAfterMaxEnd(date, product, startDate, kiosk),
    );
  }

  // If no schedule for this day, everything is disabled
  return allDisabled;
};

const isOverSelectableEndLimit = (start, end, product, kiosk) =>{
  const limit = selectableEndMaxParkingTime(product, kiosk);
  if (!limit || !start || !end) {
    return false;
  }
  return end.isAfter(start.add(limit.asMilliseconds(), 'ms'), 'day');
};

const selectableEndMaxParkingTime = (product, kiosk) => {
  return product?.durationPolicy === DURATION_POLICY.SELECTABLE_END && kiosk?.flexiPmcMaxDuration ?
    dayjs.duration(kiosk.flexiPmcMaxDuration) : null;
};

const isSellingScheduleDisabledEndDate = (endDate, startDate, product) => {
  const start = dayjs(startDate);
  const schedule = product?.sellingSchedule;

  // ENABLED CASE without checking the schedule::
  // If start date is same than end date, it cannot be disabled
  // or If no schedule
  // or if end date or start date is not present
  if (isEmpty(schedule) || !endDate || !startDate || (endDate && startDate && endDate.isSame(start, 'day'))) {
    return false;
  }

  // Check the schedule for end date
  const endSchedule = findEndDateForSchedule(start, product);
  return !(endSchedule && (endSchedule.isSame(endDate, 'day') || endSchedule.isAfter(endDate, 'day')));
};

const isSellingScheduleDisabledStartDate = (date, product) => {
  if (!product || !date || isEmpty(product.sellingSchedule)) {
    return false;
  }
  return !sellingScheduleScheduleForWeekday(date, product);
};

const isSellingScheduleDisabledCurrentDay = (date, product) => {
  const now = dayjs();
  if (!now.isSame(date, 'day')) {
    return false;
  }

  const schedule = sellingScheduleScheduleForWeekday(date, product);
  if (!schedule) {
    return false;
  }
  const { endHour, endMin } = scheduleToIntValues(schedule);
  // Check whether schedule is already ended
  return now.hour()*60+now.minute()>= endHour*60+endMin;
};

const findEndDateForSchedule = (startDate, product, limit = 60) => {
  const dailySchedule = sellingScheduleScheduleForWeekday(startDate, product);
  let endDate = startDate;
  // Schedule found
  if (dailySchedule) {
    const { endHour, endMin } = scheduleToIntValues(dailySchedule);
    // If daily schedule is set to 23:59 it is a "full day"
    if (endHour === 23 && endMin === 59) {
      for (let i = 1; i <= limit; i++) {
        // Get next weekday
        const nextDate = startDate.add(i, 'day');
        const nextDaySchedule = sellingScheduleScheduleForWeekday(nextDate, product);
        // And check if it starts from 00:00 -> means that permit can continue on this day
        if (nextDaySchedule && isStartOfDay(nextDaySchedule.startTime)) {
          endDate = nextDate;
        } else {
          break;
        }
      }
    }
  }
  return endDate;
};

const sellingScheduleScheduleForWeekday = (date, product) => {
  const schedule = product?.sellingSchedule;
  if (isEmpty(schedule) || !date) {
    return false;
  }
  const weekday = date.format('dddd').toUpperCase();
  const dailySchedule = schedule?.find(schedule => schedule.dayOfWeek === weekday);
  if (dailySchedule) {
    return dailySchedule;
  }
};

const getFirstAvailableStartDate = (kiosk, product) => {
  if (product?.durationPolicy === DURATION_POLICY.FIXED) {
    return null;
  }
  const productStartSellingDate = dayjs(product?.startSellingDate);
  const date = createNewDayjsObject(kiosk?.countryCode);
  let schedule = sellingScheduleScheduleForWeekday(date, product);
  // First check if first available date is today
  if (schedule) {
    // Check that date fits in schedule
    if (isTimeInSchedule(date.hour(), date.minute(), schedule)) {
      return date;
    } else {
      // Current time does not fit in schedule, it is either in the past or in future
      const { startHour, startMin, startSeconds } = scheduleToIntValues(schedule);
      const scheduleStartDate = date.hour(startHour).minute(startMin).second(startSeconds);
      // if it is in the future, we can set the future start time,
      // otherwise there is no available start time during this day
      if (scheduleStartDate.isAfter(date)) {
        // Set result according to schedule
        return scheduleStartDate;
      }
    }
  }
  // Check next days for schedule and start time
  for (let i= 1; i<= 7; i++) {
    const dateModified = date.add(i, 'day');
    if (!isSellingScheduleDisabledStartDate(dateModified, product)) {
      schedule = sellingScheduleScheduleForWeekday(dateModified, product);
      if (schedule) {
        const { startHour, startMin } = scheduleToIntValues(schedule);
        // Set result according to schedule
        return dateModified.hour(startHour).minute(startMin).second(0);
      }
    }
  }
  if (date.isBefore(productStartSellingDate)) {
    return productStartSellingDate;
  }
  return date;
};

const isTimeInSchedule = (hour, min, schedule) => {
  const { startHour, startMin, endHour, endMin } = scheduleToIntValues(schedule);
  const time = hour*60+min;
  const startTime = startHour*60+startMin;
  const endTime = endHour*60+endMin;

  return startTime <= time && endTime >= time;
};

// The service sends the time either as HH:mm or HH:mm:ss
const isStartOfDay = time => {
  const [ hour, min ] = time.split(':').map(val => parseInt(val));
  return hour === 0 && min === 0;
};

const scheduleToIntValues = schedule => {
  const [ startHour, startMin, startSeconds = 0 ] = schedule.startTime.split(':').map(val => parseInt(val));
  const [ endHour, endMin, endSeconds = 0 ] = schedule.endTime.split(':').map(val => parseInt(val));
  return { startHour, startMin, startSeconds, endHour, endMin, endSeconds };
};

// The pickers show the time of the kiosk country, whatever the time zone of the browser is
const isTimeSelectable = (value, product, kiosk, timeZone) => {
  const kioskTime = dayjs(dayjs(value).tz(timeZone).format('YYYY-MM-DDTHH:mm'));
  const disabled = sellingScheduleDisabledTimes(kioskTime, product, kioskTime, kiosk);
  return !disabled.disabledHours().includes(kioskTime.hour()) &&
    !disabled.disabledMinutes().includes(kioskTime.minute());
};

export default {
  isTimeSelectable,
  sellingScheduleDisabledTimes,
  isOverSelectableEndLimit,
  isSellingScheduleDisabledStartDate,
  isSellingScheduleDisabledEndDate,
  isSellingScheduleDisabledCurrentDay,
  getFirstAvailableStartDate,
};
